import React from 'react';
import { useTranslation } from 'react-i18next';

const LEVEL_COLORS = ['#ef4444', '#f97316', '#eab308', '#22c55e', '#3b82f6', '#a855f7'];
const LEVEL_DETAILS = ['0j / 0h', '8j / 20h', '11j / 30h', '15j / 40h', '18j / 60h', '22j / 80h'];

/**
 * Shared activeness distribution card.
 * Props:
 *   - distribution: object { 0: count, 1: count, ..., 5: count }
 *   - totalBonus: number (optional, total bonus activeness in $)
 *   - title: string (optional override)
 */
const ActivenessDistributionCard = ({ distribution, totalBonus, title }) => {
  const { t } = useTranslation();
  const LEVEL_LABELS = [
    t('admin.shared.levelAbsent'),
    t('admin.shared.levelMinimal'),
    t('admin.shared.levelOccasional'),
    t('admin.shared.levelFrequent'),
    t('admin.shared.levelIntense'),
    t('admin.shared.levelMaximum'),
  ];
  if (!distribution) return null;

  const totalCreators = Object.values(distribution).reduce((s, c) => s + c, 0) || 1;

  const fmtUSD = (n) => {
    if (n == null) return '$0';
    return '$' + Math.round(n).toLocaleString('fr-FR');
  };

  return (
    <div className="rounded-xl border" style={{ backgroundColor: '#111111', borderColor: '#1f1f1f', padding: 'clamp(12px, 3vw, 20px)' }}>
      <p className="text-xs font-semibold mb-4 uppercase tracking-wider" style={{ color: 'rgba(255,255,255,0.4)', fontFamily: 'Sora, sans-serif' }}>
        {title || t('admin.shared.activenessTitle')}
      </p>
      <div className="space-y-3 mb-3">
        {Object.entries(distribution).map(([level, count]) => {
          const pct = (count / totalCreators) * 100;
          const color = LEVEL_COLORS[level] || '#666';
          const label = LEVEL_LABELS[level] || `L${level}`;
          const detail = LEVEL_DETAILS[level] || '';
          return (
            <div key={level}>
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-bold px-1.5 py-0.5 rounded" style={{ backgroundColor: `${color}20`, color, fontFamily: 'Sora, sans-serif', minWidth: '28px', textAlign: 'center' }}>
                    L{level}
                  </span>
                  <span className="text-[10px]" style={{ color: 'rgba(255,255,255,0.35)' }}>{label}</span>
                  <span className="text-[9px]" style={{ color: 'rgba(255,255,255,0.2)' }}>({detail})</span>
                </div>
                <span className="text-xs font-bold" style={{ color: 'white', fontFamily: 'Sora, sans-serif' }}>
                  {count} <span style={{ color: 'rgba(255,255,255,0.25)', fontWeight: 400 }}>({Math.round(pct)}%)</span>
                </span>
              </div>
              <div className="w-full h-2 rounded-full overflow-hidden" style={{ backgroundColor: 'rgba(255,255,255,0.06)' }}>
                <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: color }} />
              </div>
            </div>
          );
        })}
      </div>
      {totalBonus != null && (
        <div className="pt-2 border-t" style={{ borderColor: '#1f1f1f' }}>
          <p className="text-xs" style={{ color: 'rgba(255,255,255,0.4)', fontFamily: 'Inter, sans-serif' }}>
            {t('admin.shared.totalBonusActiveness')} <span className="text-white font-semibold">{fmtUSD(totalBonus)}</span>
          </p>
        </div>
      )}
    </div>
  );
};

export default ActivenessDistributionCard;
